// client/src/services/mapService.js - Vollständig auf OpenStreetMap umgestellt
import 'leaflet/dist/leaflet.css';

// Nominatim für die Geocodierung (Adresse → Koordinaten)
const NOMINATIM_BASE_URL = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE_URL = 'https://nominatim.openstreetmap.org/reverse';
// Respektiere die Nutzungsbedingungen von Nominatim
const APP_NAME = 'Snus-Finder-DE';
const APP_VERSION = '1.0.0';

// Singleton-Instanz, um sicherzustellen, dass Leaflet nur einmal geladen wird
let leafletPromise = null;
let L = null; // Leaflet-Instanz

/**
 * Lädt die Leaflet-Bibliothek und stellt sicher, dass sie nur einmal initialisiert wird
 */
export const loadLeaflet = async () => {
    if (L) {
        return L;
    }

    if (leafletPromise) {
        return leafletPromise;
    }

    leafletPromise = new Promise(async (resolve) => {
        // Dynamisch importieren
        const leaflet = await import('leaflet');
        L = leaflet.default;

        // Korrigiere den Pfad zu den Marker-Icons
        delete L.Icon.Default.prototype._getIconUrl;
        L.Icon.Default.mergeOptions({
            iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
            iconUrl: require('leaflet/dist/images/marker-icon.png'),
            shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
        });

        resolve(L);
    });

    return leafletPromise;
};

/**
 * Initialisiert eine Karte im angegebenen DOM-Element
 * @param {HTMLElement} element - DOM-Element für die Karte
 * @param {Object} options - Optionen für die Karte
 * @returns {Object} - Die erstellte Karte
 */
export const initializeMap = async (element, options = {}) => {
    if (!element) {
        throw new Error('DOM-Element für die Karte ist erforderlich');
    }

    const L = await loadLeaflet();

    // Standard-Optionen
    const defaultOptions = {
        center: [51.1657, 10.4515], // Deutschland-Zentrum
        zoom: 6,
        minZoom: 3,
        maxZoom: 19,
        scrollWheelZoom: true,
        zoomControl: true
    };

    // Optionen zusammenführen
    const mapOptions = { ...defaultOptions, ...options };

    // Hinzugefügtes Logging vor map Erstellung
    console.log(`[mapService] Initializing Leaflet map in element:`, element);
    console.log(`[mapService] With options:`, mapOptions);

    // Karte erstellen
    const map = L.map(element, mapOptions);

    // Hinzugefügtes Logging nach map Erstellung
    if (map) {
        console.log(`[mapService] Leaflet map instance created successfully:`, map);
    } else {
        console.error(`[mapService] Failed to create Leaflet map instance!`);
        throw new Error('Leaflet map instance could not be created.'); // Fehler werfen, um es deutlicher zu machen
    }

    // Kartenlayer hinzufügen
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxZoom: 19
    }).addTo(map);

    return map;
};

/**
 * Fügt Marker zur Karte hinzu oder aktualisiert sie in einer FeatureGroup
 * @param {Object} map - Leaflet-Karte
 * @param {Array} shops - Array mit Shop-Daten
 * @param {Object} markerLayer - Leaflet FeatureGroup zum Hinzufügen der Marker
 * @returns {Array} - Array der erstellten Marker
 */
export const addShopMarkers = async (map, shops, markerLayer) => {
    if (!map || !shops || !Array.isArray(shops) || !markerLayer) {
        console.warn('[mapService] addShopMarkers: Missing map, shops array, or markerLayer.');
        return [];
    }

    const L = await loadLeaflet();
    const markers = [];
    const bounds = L.latLngBounds(); // Immer neu berechnen für die aktuellen Shops

    // Vorhandene Marker aus der Gruppe entfernen
    markerLayer.clearLayers();
    console.log('[mapService] Cleared existing markers from layer.');

    if (shops.length === 0) {
        console.log('[mapService] No shops to add markers for.');
        // Optional: Hier könnte man zu einer Standardansicht zurückzoomen,
        // aber das überlassen wir erstmal der MapView-Komponente oder dem Nutzer.
        return []; // Leeres Array zurückgeben, wenn keine Shops vorhanden sind
    }

    shops.forEach(shop => {
        if (!shop.coordinates || typeof shop.coordinates.lat !== 'number' || typeof shop.coordinates.lng !== 'number') {
             console.warn('[mapService] Skipping shop due to missing or invalid coordinates:', shop.name || shop.id);
            return; // Zum nächsten Shop springen
        }

        // Koordinaten extrahieren
        const { lat, lng } = shop.coordinates;
        const position = L.latLng(lat, lng);

        // Bounds für Auto-Zoom erweitern
        bounds.extend(position);

        // Marker erstellen
        const marker = L.marker(position); // Nicht sofort zur Karte hinzufügen

        // Popup mit Shop-Informationen
        const popupContent = `
      <div class="map-popup">
        <h3>${shop.name || 'Snus Shop'}</h3>
        <p>${shop.address || ''}, ${shop.postalCode || ''} ${shop.city || ''}</p>
        <p>Bewertung: ${(shop.rating || 0).toFixed(1)} (${shop.reviewCount || 0} Bewertungen)</p>
        <a href="/shops/${shop.id}" class="popup-link">Details ansehen</a>
      </div>
    `;

        marker.bindPopup(popupContent);
        markers.push(marker); // Marker sammeln

        // Marker zur FeatureGroup hinzufügen
        markerLayer.addLayer(marker);
    });

    console.log(`[mapService] Added ${markers.length} new markers to the layer.`);

    // Auto-Zoom, wenn Marker vorhanden sind und die Bounds gültig sind
    const areBoundsValid = bounds.isValid();
    console.log(`[mapService] Checking bounds: ${markers.length} markers, bounds valid: ${areBoundsValid}`);

    if (markers.length > 0 && areBoundsValid) {
        const fitBoundsOptions = { padding: [50, 50], maxZoom: 16 };
        console.log('[mapService] Attempting fitBounds with bounds:', bounds.toBBoxString(), 'and options:', fitBoundsOptions);
        try {
             map.fitBounds(bounds, fitBoundsOptions);
             console.log('[mapService] fitBounds executed. New zoom:', map.getZoom(), 'New bounds:', map.getBounds().toBBoxString());
        } catch (e) {
             console.error('[mapService] Error executing fitBounds:', e);
        }
    } else if (markers.length > 0) {
        console.warn('[mapService] Could not fit bounds because bounds are invalid.');
    } else {
        console.log('[mapService] No markers, skipping fitBounds.');
    }


    return markers; // Gibt die erstellten Marker zurück (optional nützlich)
};

/**
 * Geokodiert eine Adresse in Koordinaten mit Nominatim
 * @param {string} address - Die zu geokodierende Adresse
 * @returns {Promise<Object>} - Koordinaten {lat, lng}
 */
export const geocodeAddress = async (address) => {
    if (!address) {
        return { lat: 0, lng: 0 };
    }

    try {
        // Nominatim API respektieren (max. 1 Anfrage pro Sekunde)
        const params = new URLSearchParams({
            q: address,
            format: 'json',
            addressdetails: 1,
            limit: 1,
            countrycodes: 'de', // Deutschland
        });

        // Füge User-Agent hinzu, wie von Nominatim empfohlen
        const response = await fetch(`${NOMINATIM_BASE_URL}?${params.toString()}`, {
            headers: {
                'User-Agent': `${APP_NAME}/${APP_VERSION}`
            }
        });

        if (!response.ok) {
            throw new Error(`Geokodierung fehlgeschlagen: ${response.status}`);
        }

        const data = await response.json();

        if (data && data.length > 0) {
            return {
                lat: parseFloat(data[0].lat),
                lng: parseFloat(data[0].lon)
            };
        }

        return { lat: 0, lng: 0 };
    } catch (error) {
        console.error('Fehler bei der Geokodierung:', error);
        return { lat: 0, lng: 0 };
    }
};

/**
 * Reverse-Geokodierung: Koordinaten in Adresse umwandeln
 * @param {number} lat - Breitengrad
 * @param {number} lng - Längengrad
 * @returns {Promise<Object>} - Adressinformationen
 */
export const reverseGeocode = async (lat, lng) => {
    try {
        const params = new URLSearchParams({
            lat,
            lon: lng,
            format: 'json',
            addressdetails: 1,
            zoom: 18,
            'accept-language': 'de'
        });

        const response = await fetch(`${NOMINATIM_REVERSE_URL}?${params.toString()}`, {
            headers: {
                'User-Agent': `${APP_NAME}/${APP_VERSION}`
            }
        });

        if (!response.ok) {
            throw new Error(`Reverse-Geokodierung fehlgeschlagen: ${response.status}`);
        }

        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Fehler bei der Reverse-Geokodierung:', error);
        return null;
    }
};

/**
 * Sucht nach Standorten basierend auf einem Suchbegriff mit Nominatim
 * @param {string} query - Suchbegriff
 * @returns {Promise<Array>} - Array mit Suchergebnissen
 */
export const searchLocations = async (query) => {
    if (!query || query.length < 2) {
        return [];
    }

    try {
        const params = new URLSearchParams({
            q: query,
            format: 'json',
            addressdetails: 1,
            limit: 5,
            countrycodes: 'de', // Deutschland
            featuretype: 'city'
        });

        const response = await fetch(`${NOMINATIM_BASE_URL}?${params.toString()}`, {
            headers: {
                'User-Agent': `${APP_NAME}/${APP_VERSION}`
            }
        });

        if (!response.ok) {
            throw new Error(`Standortsuche fehlgeschlagen: ${response.status}`);
        }

        const data = await response.json();

        // Formatierte Ergebnisse zurückgeben
        return data.map(item => ({
            id: item.place_id,
            description: `${item.display_name}`,
            lat: parseFloat(item.lat),
            lng: parseFloat(item.lon)
        }));
    } catch (error) {
        console.error('Fehler bei der Standortsuche:', error);
        return [];
    }
};

/**
 * Berechnet die Entfernung zwischen zwei Koordinaten (Haversine-Formel)
 * @param {number} lat1 - Erste Breitengrad
 * @param {number} lon1 - Erste Längengrad
 * @param {number} lat2 - Zweite Breitengrad
 * @param {number} lon2 - Zweite Längengrad
 * @returns {number} - Entfernung in Kilometern
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Erdradius in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
};

/**
 * Hilfsfunktion: Umrechnung von Grad in Bogenmaß
 */
function deg2rad(deg) {
    return deg * (Math.PI/180);
}

/**
 * Findet Shops in der Nähe eines bestimmten Standorts
 * @param {Array} shops - Liste aller Shops
 * @param {number} lat - Breitengrad des Standorts
 * @param {number} lng - Längengrad des Standorts
 * @param {number} radius - Suchradius in Kilometern
 * @returns {Array} - Gefilterte Liste der Shops in der Nähe mit Entfernungsangabe
 */
export const findShopsNearby = (shops, lat, lng, radius = 10) => {
    if (!shops || !Array.isArray(shops) || !lat || !lng) {
        return [];
    }

    // Für jeden Shop die Entfernung berechnen und filtern
    return shops
        .map(shop => {
            if (!shop.coordinates || !shop.coordinates.lat || !shop.coordinates.lng) {
                return null;
            }

            const distance = calculateDistance(
                lat, lng,
                shop.coordinates.lat,
                shop.coordinates.lng
            );

            return { ...shop, distance };
        })
        .filter(shop => shop !== null && shop.distance <= radius)
        .sort((a, b) => a.distance - b.distance);
};